import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { chatAPI } from '../utils/api';
import { firestoreDb } from '../utils/firebase';
import { collection, query, where, orderBy, limit, onSnapshot } from 'firebase/firestore';

const ChatUnreadContext = createContext({ totalUnread: 0, clearChatUnread: () => {}, setActiveChat: () => {} });

export const ChatUnreadProvider = ({ children }) => {
  const { user, firebaseReady } = useAuth();
  const [unreadByChat, setUnreadByChat] = useState({});
  const [chatIds, setChatIds] = useState([]);
  const activeChatRef = useRef(null);
  const listenFromRef = useRef(new Date().toISOString());

  const totalUnread = Object.values(unreadByChat).reduce((s, n) => s + n, 0);

  const clearChatUnread = useCallback((chatId) => {
    if (!chatId) return;
    setUnreadByChat(prev => ({ ...prev, [chatId]: 0 }));
  }, []);

  const setActiveChat = useCallback((chatId) => {
    activeChatRef.current = chatId;
    if (chatId) clearChatUnread(chatId);
  }, [clearChatUnread]);

  // Load initial unread counts from chat list
  useEffect(() => {
    if (!user?.user_id) {
      setUnreadByChat({});
      setChatIds([]);
      return;
    }
    chatAPI.getChats().then(res => {
      const unreads = {};
      const ids = [];
      (res.data || []).forEach(c => {
        unreads[c.chat_id] = c.unread_count || 0;
        ids.push(c.chat_id);
      });
      setUnreadByChat(unreads);
      setChatIds(ids);
    }).catch(() => {});
  }, [user?.user_id]);

  // Firestore real-time listener for new messages across all user chats.
  // `created_at >= listenFromRef` pushes the timestamp filter to Firestore so the
  // initial snapshot contains 0 historical messages — no wasted reads.
  useEffect(() => {
    if (!firebaseReady || chatIds.length === 0 || !user?.user_id) return;
    const q = query(
      collection(firestoreDb, 'messages'),
      where('chat_id', 'in', chatIds),
      where('created_at', '>=', listenFromRef.current)
    );
    const unsubscribe = onSnapshot(q, (snapshot) => {
      snapshot.docChanges().forEach((change) => {
        if (change.type !== 'added') return;
        const msg = change.doc.data();
        // Skip historical messages that existed before this page loaded
        if (msg.created_at < listenFromRef.current) return;
        // Skip messages sent by the current user
        if (msg.sender_id === user.user_id) return;
        // Skip messages for the chat the user is actively viewing
        if (msg.chat_id === activeChatRef.current) return;
        setUnreadByChat(prev => ({
          ...prev,
          [msg.chat_id]: (prev[msg.chat_id] || 0) + 1,
        }));
      });
    }, () => {});
    return () => unsubscribe();
  }, [chatIds.join(','), user?.user_id, firebaseReady]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <ChatUnreadContext.Provider value={{ totalUnread, clearChatUnread, setActiveChat }}>
      {children}
    </ChatUnreadContext.Provider>
  );
};

export const useChatUnread = () => useContext(ChatUnreadContext);
